import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { financeDashboardService, reminderService, productService } from '../../services/financeService';
import {
  ReceiptLong as InvoiceIcon,
  ShoppingCart as PurchaseIcon,
  LocalShipping as GatePassIcon,
  Category as ProductIcon,
  People as CustomerIcon,
  Payments as ExpenseIcon,
  AccountBalanceWallet as PaymentIcon,
  Inventory2 as BatchIcon,
  Description as GDIcon,
  TrendingUp as RevenueIcon,
  TrendingDown as ExpensesIcon,
  AccountBalance as ProfitIcon,
  CallReceived as ReceivableIcon,
  CallMade as PayableIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import './Financial.css';

const QUICK_LINKS = [
  { label: 'Invoices', path: '/invoices', Icon: InvoiceIcon },
  { label: 'Purchases', path: '/purchases', Icon: PurchaseIcon },
  { label: 'Gate Passes', path: '/gate-passes', Icon: GatePassIcon },
  { label: 'Products', path: '/products', Icon: ProductIcon },
  { label: 'Customers', path: '/customers', Icon: CustomerIcon },
  { label: 'Expenses', path: '/expenses', Icon: ExpenseIcon },
  { label: 'Payments', path: '/payments', Icon: PaymentIcon },
  { label: 'Batches', path: '/batches', Icon: BatchIcon },
  { label: 'GDs', path: '/gd', Icon: GDIcon },
];

// Big KPI amount: small "PKR" unit + the figure, which never breaks across lines.
const Money = ({ amount }) => {
  const n = Number(amount) || 0;
  const figure = (n < 0 ? '-' : '') + Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
  return (
    <span className="metric-value">
      <span className="metric-unit">PKR</span>
      <span className="metric-figure">{figure}</span>
    </span>
  );
};

// Bars grow from a shared baseline; a month with no amount shows only the baseline tick.
const MonthlyBars = ({ data, variant, formatCurrency, emptyText }) => {
  const rows = data || [];
  const values = rows.map(m => Math.max(Number(m.amount) || 0, 0));
  const maxValue = Math.max(0, ...values);
  if (!rows.length || maxValue === 0) {
    return <div className="chart-empty">{emptyText}</div>;
  }
  return (
    <div className="chart-bars">
      {rows.map((item, index) => {
        const height = (values[index] / maxValue) * 100;
        return (
          <div key={index} className="bar-container" title={`${item.month}: ${formatCurrency(item.amount)}`}>
            <div className="bar-track">
              <div className={`bar ${variant}-bar`} style={{ height: `${height}%` }}></div>
            </div>
            <span className="bar-label">{item.month}</span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Financial Dashboard Component
 * Displays financial metrics, charts, and summaries
 */
const FinancialDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [lowStockItems, setLowStockItems] = useState([]);

  useEffect(() => {
    fetchDashboard();
    fetchReminders();
    fetchLowStock();
  }, []);

  const fetchLowStock = async () => {
    try {
      const res = await productService.getAll({ activeOnly: true });
      const products = res?.data || res || [];
      const low = Array.isArray(products)
        ? products.filter(p => p.reorderLevel != null && p.currentStock != null && Number(p.currentStock) <= Number(p.reorderLevel))
        : [];
      setLowStockItems(low);
    } catch (e) {
      // non-critical
    }
  };

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await financeDashboardService.getDashboard();
      if (response.success) {
        setDashboard(response.data);
      } else {
        setError(response.message || 'Failed to load dashboard');
      }
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchReminders = async () => {
    try {
      const data = await reminderService.getAll();
      setReminders(data);
    } catch (err) {
      console.error('Failed to load reminders:', err);
    }
  };

  // One format everywhere on this page ("PKR 1,234" / "-PKR 1,234") — Intl's en-PK output
  // ("Rs 1,234") didn't match the "PKR 0" fallback or the rest of the app.
  const formatCurrency = (amount) => {
    const n = Number(amount) || 0;
    const abs = Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
    return n < 0 ? `-PKR ${abs}` : `PKR ${abs}`;
  };

  const formatPercent = (value) => {
    if (!value) return '0%';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="financial-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>Loading financial dashboard...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="financial-dashboard">
        <div className="error-message">
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <button onClick={fetchDashboard} className="btn-primary">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="financial-dashboard">
      <div className="dashboard-header">
        <h2>Financial Dashboard</h2>
        <button onClick={() => { fetchDashboard(); fetchReminders(); fetchLowStock(); }} className="btn-secondary">
          <RefreshIcon sx={{ fontSize: 18 }} aria-hidden="true" />
          Refresh
        </button>
      </div>

      {/* Quick Navigation */}
      <nav className="quick-links" aria-label="Quick links">
        {QUICK_LINKS.map(({ label, path, Icon }) => (
          <button key={path} onClick={() => navigate(path)} className="quick-link">
            <Icon aria-hidden="true" />
            {label}
          </button>
        ))}
      </nav>

      {/* Compact Alerts Banner — full detail is on the Reminders page */}
      {reminders && (reminders.overdueCount > 0 || reminders.dueTodayCount > 0 || reminders.dueSoonCount > 0) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: reminders.overdueCount > 0 ? 'var(--color-error-light)' : 'var(--color-warning-light)',
          border: `1px solid ${reminders.overdueCount > 0 ? 'var(--color-error-border)' : 'var(--color-warning-border)'}`,
          borderRadius: '10px',
          padding: '12px 18px',
          marginBottom: '20px',
          gap: '12px',
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '1.2rem', color: reminders.overdueCount > 0 ? 'var(--color-error)' : 'var(--color-text-secondary)' }}>!</span>
            <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--color-neutral-700)' }}>Pending Actions:</span>
            {reminders.overdueCount > 0 && (
              <span style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', borderRadius: '9999px', padding: '2px 10px', fontSize: '0.8rem', fontWeight: 700 }}>
                {reminders.overdueCount} Overdue
              </span>
            )}
            {reminders.dueTodayCount > 0 && (
              <span style={{ background: 'var(--color-warning-light)', color: 'var(--color-warning)', borderRadius: '9999px', padding: '2px 10px', fontSize: '0.8rem', fontWeight: 700 }}>
                {reminders.dueTodayCount} Due Today
              </span>
            )}
            {reminders.dueSoonCount > 0 && (
              <span style={{ background: 'var(--color-info-light)', color: 'var(--color-primary)', borderRadius: '9999px', padding: '2px 10px', fontSize: '0.8rem', fontWeight: 600 }}>
                {reminders.dueSoonCount} Due Soon
              </span>
            )}
          </div>
          <button
            onClick={() => navigate('/reminders')}
            className="btn-secondary"
            style={{ fontSize: '0.8rem', padding: '6px 14px', whiteSpace: 'nowrap' }}
          >
            View Reminders →
          </button>
        </div>
      )}

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div style={{
          background: 'var(--color-warning-light)',
          border: '1px solid var(--color-warning-border)',
          borderRadius: '10px',
          padding: '12px 18px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--color-warning-strong)' }}>LOW STOCK</span>
            <span style={{ fontWeight: 700, color: 'var(--color-warning-strong)', fontSize: '0.9rem' }}>
              {lowStockItems.length} product{lowStockItems.length > 1 ? 's' : ''} below reorder level:
            </span>
            {lowStockItems.slice(0, 4).map(p => (
              <span key={p.id} style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', borderRadius: '9999px', padding: '2px 10px', fontSize: '0.78rem', fontWeight: 600 }}>
                {p.name} ({Number(p.currentStock).toFixed(0)} {p.unit} / min {Number(p.reorderLevel).toFixed(0)})
              </span>
            ))}
            {lowStockItems.length > 4 && (
              <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem' }}>+{lowStockItems.length - 4} more</span>
            )}
          </div>
          <button onClick={() => navigate('/products')} className="btn-secondary" style={{ fontSize: '0.8rem', padding: '6px 14px', whiteSpace: 'nowrap' }}>
            View Products →
          </button>
        </div>
      )}

      {/* Key Metrics Cards */}
      <div className="metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-icon"><RevenueIcon aria-hidden="true" /></div>
          <div className="metric-content">
            <span className="metric-label">Total Revenue (YTD)</span>
            <Money amount={dashboard?.totalRevenue} />
            <span className="metric-sub">
              This month: {formatCurrency(dashboard?.totalRevenueThisMonth)}
              {dashboard?.revenueGrowthPercent && (
                <span className={`growth ${dashboard.revenueGrowthPercent >= 0 ? 'positive' : 'negative'}`}>
                  {formatPercent(dashboard.revenueGrowthPercent)}
                </span>
              )}
            </span>
          </div>
        </div>

        <div className="metric-card expenses">
          <div className="metric-icon"><ExpensesIcon aria-hidden="true" /></div>
          <div className="metric-content">
            <span className="metric-label">Total Expenses (YTD)</span>
            <Money amount={dashboard?.totalExpenses} />
            <span className="metric-sub">
              This month: {formatCurrency(dashboard?.totalExpensesThisMonth)}
            </span>
          </div>
        </div>

        <div className="metric-card profit">
          <div className="metric-icon"><ProfitIcon aria-hidden="true" /></div>
          <div className="metric-content">
            <span className="metric-label">Net Profit (YTD)</span>
            <Money amount={dashboard?.netProfit} />
            <span className="metric-sub">
              Margin: {dashboard?.profitMarginPercent?.toFixed(1) || 0}%
            </span>
          </div>
        </div>

        <div className="metric-card outstanding">
          <div className="metric-icon"><ReceivableIcon aria-hidden="true" /></div>
          <div className="metric-content">
            <span className="metric-label">Customer Receivables</span>
            <Money amount={dashboard?.totalOutstanding} />
            <span className="metric-sub">
              Owed to you · {dashboard?.overdueInvoices || 0} overdue invoices
            </span>
          </div>
        </div>

        <div className="metric-card outstanding">
          <div className="metric-icon"><PayableIcon aria-hidden="true" /></div>
          <div className="metric-content">
            <span className="metric-label">Vendor Payables</span>
            <Money amount={dashboard?.totalPayables} />
            <span className="metric-sub">
              Owed by you to vendors
            </span>
          </div>
        </div>
      </div>

      {/* Invoice & Expense Summary */}
      <div className="summary-grid">
        <div className="summary-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/invoices')}>
          <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Invoice Summary <span style={{ fontSize: '0.75rem', color: 'var(--color-accent-hover)', fontWeight: 500 }}>View all →</span>
          </h3>
          <div className="summary-stats">
            <div className="stat-row">
              <span className="stat-label">Draft</span>
              <span className="stat-value badge-draft">{dashboard?.draftInvoices || 0}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Sent</span>
              <span className="stat-value badge-sent">{dashboard?.sentInvoices || 0}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Paid</span>
              <span className="stat-value badge-paid">{dashboard?.paidInvoices || 0}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Overdue</span>
              <span className="stat-value badge-overdue">{dashboard?.overdueInvoices || 0}</span>
            </div>
          </div>
        </div>

        <div className="summary-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/expenses')}>
          <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Expense Summary <span style={{ fontSize: '0.75rem', color: 'var(--color-accent-hover)', fontWeight: 500 }}>View all →</span>
          </h3>
          <div className="summary-stats">
            <div className="stat-row">
              <span className="stat-label">Pending Approval</span>
              <span className="stat-value badge-pending">{dashboard?.pendingExpenses || 0}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Approved</span>
              <span className="stat-value badge-approved">{dashboard?.approvedExpenses || 0}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Total Paid Out</span>
              <span className="stat-value">{formatCurrency(dashboard?.totalPaid)}</span>
            </div>
          </div>
        </div>

        <div className="summary-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/payments')}>
          <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Cash Flow <span style={{ fontSize: '0.75rem', color: 'var(--color-accent-hover)', fontWeight: 500 }}>Payments →</span>
          </h3>
          <div className="summary-stats">
            <div className="stat-row">
              <span className="stat-label">Received</span>
              <span className="stat-value text-success">{formatCurrency(dashboard?.totalReceived)}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Paid</span>
              <span className="stat-value text-danger">{formatCurrency(dashboard?.totalPaid)}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Net Cash Flow</span>
              <span className={`stat-value ${dashboard?.cashFlow >= 0 ? 'text-success' : 'text-danger'}`}>
                {formatCurrency(dashboard?.cashFlow)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Charts */}
      <div className="charts-grid">
        <div className="chart-card">
          <h3>Monthly Revenue</h3>
          <MonthlyBars data={dashboard?.monthlyRevenue} variant="revenue" formatCurrency={formatCurrency}
            emptyText="No revenue recorded in the last six months." />
        </div>

        <div className="chart-card">
          <h3>Monthly Expenses</h3>
          <MonthlyBars data={dashboard?.monthlyExpenses} variant="expense" formatCurrency={formatCurrency}
            emptyText="No expenses recorded in the last six months." />
        </div>
      </div>

      {/* Expenses by Category */}
      {dashboard?.expensesByCategory && Object.keys(dashboard.expensesByCategory).length > 0 && (
        <div className="category-card">
          <h3>Expenses by Category</h3>
          <div className="category-list">
            {Object.entries(dashboard.expensesByCategory)
              .sort((a, b) => b[1] - a[1])
              .map(([category, amount], index) => {
                const total = Object.values(dashboard.expensesByCategory).reduce((a, b) => a + b, 0);
                const percent = total > 0 ? (amount / total) * 100 : 0;
                return (
                  <div key={index} className="category-row">
                    <span className="category-name">{category}</span>
                    <div className="category-bar-container">
                      <div className="category-bar" style={{ width: `${percent}%` }}></div>
                    </div>
                    <span className="category-value">{formatCurrency(amount)}</span>
                    <span className="category-percent">{percent.toFixed(1)}%</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialDashboard;

